import { styled } from "@mui/material/styles";
import ButtonBase from "@mui/material/ButtonBase";
import {
  blue,
  green,
  grey,
  orange,
  pink,
  purple,
  red,
} from "@mui/material/colors";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { getStatusNumberBackgroundColor } from "../../utils/orders";
import { lighten } from "@mui/system/colorManipulator";
import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import React from "react";

export const IconStackButton = styled(ButtonBase)(({ theme }) => ({
  width: "100% !important",
  backgroundColor: grey[100],
  "& .MuiPaper-root": {
    width: "100% !important",
    color: grey[700],
  },
  "&:hover, &.Mui-focusVisible": {
    "& .MuiPaper-root": {
      width: "100% !important",
      backgroundColor: grey[100],
    },
  },
}));

export const IconStackButtonNotFlexFullHeight = styled(ButtonBase)(
  ({ theme }) => ({
    height: "100% !important",
    width: "100% !important",
    backgroundColor: grey[100],
    "& .MuiPaper-root": {
      height: "100% !important",
      width: "100% !important",
      color: grey[700],
    },
    "&:hover, &.Mui-focusVisible": {
      "& .MuiPaper-root": {
        width: "100% !important",
        backgroundColor: grey[100],
      },
    },
  })
);

export const IconStackButtonFullHeight = styled(ButtonBase)(
  ({ padding, bgColor, bgColorHover }) => ({
    height: "100% !important",
    width: "100% !important",
    "& .MuiPaper-root": {
      padding: padding || 0,
      height: "100% !important",
      width: "100% !important",
      color: grey[700],
      backgroundColor: bgColor || grey[200],
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    "&:hover, &.Mui-focusVisible": {
      "& .MuiPaper-root": {
        width: "100% !important",
        backgroundColor: bgColorHover || grey[300],
      },
    },
  })
);

export const ButtonNoShadow = styled(Button)(
  ({
    theme,
    width,
    textColor,
    fontWeight,
    backgroundColor,
    backgroundColorHover,
    borderBottomLeftRadius,
    borderTopLeftRadius,
    borderBottomRightRadius,
    borderTopRightRadius,
  }) => ({
    width: width || "auto",
    color: textColor || grey["900"],
    fontWeight: fontWeight || "normal",
    boxShadow: "none",
    backgroundColor: backgroundColor || grey[100],
    borderBottomLeftRadius: borderBottomLeftRadius ?? 14,
    borderTopLeftRadius: borderTopLeftRadius ?? 14,
    borderBottomRightRadius: borderBottomRightRadius ?? 14,
    borderTopRightRadius: borderTopRightRadius ?? 14,
    "&:hover": {
      boxShadow: "none",
      backgroundColor: backgroundColorHover || grey[200],
    },
  })
);

export const ButtonLightGrey = styled(Button)(({ theme }) => ({
  color: grey["800"],
  fontWeight: "bold",
  boxShadow: "none",
  borderRadius: 14,
  backgroundColor: grey[100],
  "&:hover": {
    boxShadow: "none",
    backgroundColor: grey[200],
  },
}));

export const ButtonDarkGrey = styled(Button)(({ theme }) => ({
  color: grey["800"],
  fontWeight: "bold",
  boxShadow: "none",
  borderRadius: 14,
  backgroundColor: grey[200],
  "&:hover": {
    boxShadow: "none",
    backgroundColor: grey[100],
  },
}));

export const ButtonInfoNoShadow = styled(Button)(({ theme }) => ({
  color: grey["50"],
  fontWeight: "bold",
  boxShadow: "none",
  borderRadius: 14,
  backgroundColor: blue[600],
  borderColor: blue[700],
  "&:hover": {
    boxShadow: "none",
    backgroundColor: blue[500],
  },
}));

export const ButtonWarningNoShadow = styled(Button)(({ theme }) => ({
  color: grey["50"],
  fontWeight: 700,
  boxShadow: "none",
  borderRadius: 14,
  backgroundColor: orange[800],
  borderColor: orange[900],
  "&:hover": {
    boxShadow: "none",
    backgroundColor: orange[600],
  },
}));

export const ButtonSuccessNoShadow = styled(Button)(({ theme }) => ({
  color: grey["50"],
  fontWeight: "bold",
  boxShadow: "none",
  borderRadius: 14,
  backgroundColor: green[600],
  borderColor: green[800],
  "&:hover": {
    boxShadow: "none",
    backgroundColor: green[500],
  },
}));

export const ButtonDangerNoShadow = styled(Button)(({ theme }) => ({
  color: grey["50"],
  fontWeight: "bold",
  boxShadow: "none",
  borderRadius: 14,
  backgroundColor: red[700],
  borderColor: red[800],
  "&:hover": {
    boxShadow: "none",
    backgroundColor: red[600],
    borderColor: red[600],
  },
}));

export const TextButtonDanger = styled(Button)(({ theme }) => ({
  color: red["800"],
  fontWeight: "bold",
  boxShadow: "none",
  borderRadius: 14,
  backgroundColor: grey[200],
  "&:hover": {
    boxShadow: "none",
    backgroundColor: grey[100],
  },
}));

export const TextButtonInfo = styled(Button)(({ theme, bgcolor }) => ({
  color: blue["800"],
  fontWeight: "bold",
  boxShadow: "none",
  borderRadius: 14,
  backgroundColor: bgcolor || grey[200],
  "&:hover": {
    boxShadow: "none",
    backgroundColor: grey[100],
  },
}));

export const ButtonPurple = styled(Button)(({ theme }) => ({
  color: grey["50"],
  fontWeight: "bold",
  boxShadow: "none",
  borderRadius: 14,
  backgroundColor: purple[400],
  borderColor: purple[500],
  "&:hover": {
    boxShadow: "none",
    backgroundColor: purple[300],
  },
}));

export const ButtonPink = styled(Button)(({ theme }) => ({
  color: grey["50"],
  fontWeight: "bold",
  boxShadow: "none",
  borderRadius: 14,
  backgroundColor: pink[400],
  "&:hover": {
    boxShadow: "none",
    backgroundColor: pink[600],
  },
}));

export const IconButtonBackgroundLightGrey = styled(IconButton)(
  ({ theme, color, padding }) => ({
    color: color || grey[800],
    padding: padding ?? 8,
    fontWeight: "bold",
    boxShadow: "none",
    borderRadius: 14,
    backgroundColor: grey[200],
    "&:hover": {
      color: color ? lighten(color, 0.5) : grey[600],
      boxShadow: "none",
      backgroundColor: grey[100],
    },
  })
);

export const IconButtonBackgroundColored = styled(IconButton)(
  ({ theme, color, bgcolor, padding }) => ({
    color: color || grey[800],
    padding: padding ?? 8,
    fontWeight: "bold",
    boxShadow: "none",
    borderRadius: 14,
    backgroundColor: bgcolor || grey[200],
    "&:hover": {
      color: color ? lighten(color, 0.5) : grey[600],
      boxShadow: "none",
      backgroundColor: bgcolor ? lighten(bgcolor, 0.5) : grey[100],
    },
  })
);

export const IconButtonDarkGrey = styled(IconButton)(({ theme }) => ({
  color: grey["800"],
  fontWeight: "bold",
  boxShadow: "none",
  borderRadius: 14,
  backgroundColor: grey[50],
  "&:hover": {
    boxShadow: "none",
    backgroundColor: grey[100],
  },
}));

export const OrderStatusIconButton = styled(IconButton)(({ statusNumber }) => ({
  padding: "3px !important",
  backgroundColor: getStatusNumberBackgroundColor(statusNumber),
  "&:hover": {
    boxShadow: "none",
    backgroundColor: grey[100],
  },
}));

export const ModernButton = ({
  onClick,
  variant = "contained",
  size = "large",
  fullWidth = false,
  disabled = false,
  children,
  endIcon,
  color = "primary",
  sx = {},
}) => {
  const theme = useTheme();

  const buttonVariants = {
    hover: {
      scale: 1.02,
      boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
    },
    tap: {
      scale: 0.98,
    },
  };

  const getButtonColors = () => {
    switch (color) {
      case "error":
        return {
          main: theme.palette.error.main,
          dark: theme.palette.error.dark,
          text: "white",
        };
      case "success":
        return {
          main: theme.palette.success.main,
          dark: theme.palette.success.dark,
          text: "white",
        };
      case "pink":
        return {
          main: pink[400],
          dark: pink[600],
          text: grey[50],
        };
      case "secondary":
        return {
          main: theme.palette.secondary.main,
          dark: theme.palette.secondary.dark,
          text: "white",
        };
      default:
        return {
          main: theme.palette.primary.main,
          dark: theme.palette.primary.dark,
          text: "white",
        };
    }
  };

  const colors = getButtonColors();

  return (
    <motion.div
      variants={buttonVariants}
      whileHover="hover"
      whileTap="tap"
      style={{ width: fullWidth ? "100%" : "auto" }}
    >
      <Button
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        onClick={onClick}
        sx={{
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 600,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          background:
            variant === "contained"
              ? `linear-gradient(135deg, ${colors.main} 0%, ${colors.dark} 100%)`
              : undefined,
          border:
            variant === "outlined"
              ? `1px solid ${alpha(colors.main, 0.3)}`
              : undefined,
          "&:hover": {
            background:
              variant === "contained"
                ? `linear-gradient(135deg, ${colors.dark} 0%, ${colors.main} 100%)`
                : variant === "outlined"
                ? alpha(colors.main, 0.04)
                : undefined,
            boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
          },
          "&:disabled": {
            background: alpha(theme.palette.action.disabled, 0.1),
            boxShadow: "none",
          },
          ...sx,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: variant === "contained" ? colors.text : colors.main,
            }}
          >
            {children}
          </Typography>
          {endIcon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: variant === "contained" ? colors.text : colors.main,
              }}
            >
              {endIcon}
            </Box>
          )}
        </Stack>
      </Button>
    </motion.div>
  );
};
